import {
	CreatePersonTaxInformationDto,
	GetPersonTaxInformationRes,
} from '@dealstack/shared/lib/generated';

import { TaxResidenceEntry, TaxResidencesDiff } from './useJoinAndAllocationOtherDetails.types';

export const TAX_RESIDENCES_FIELD = 'taxResidences';

export const createEmptyTaxResidence = (): TaxResidenceEntry => ({
	taxResidency: '',
	tin: '',
});

/**
 * Field-name builder for a tax residence entry, e.g. `taxResidences.0.tin`.
 * Returns a plain string (matching the `*FieldName: string` props on
 * SingleTaxDetails and the react-hook-form-mantine inputs).
 */
export const taxResidenceFieldName = (index: number, key: keyof TaxResidenceEntry): string =>
	`${TAX_RESIDENCES_FIELD}.${index}.${key}`;

/**
 * One representative record per country. The backend unique is
 * (personId, countryCode, authority), so an authority-stamped record (deal
 * directory's TIN validator) can coexist with an authority-less one for the
 * same country. The form keys rows by country, so it shows one row — the
 * authority-less record, the one investor flows write to.
 */
export const dedupeSavedTaxResidencesByCountry = (
	saved: GetPersonTaxInformationRes[]
): GetPersonTaxInformationRes[] => {
	const byCountry = new Map<string, GetPersonTaxInformationRes>();
	for (const tax of saved) {
		const current = byCountry.get(tax.countryCode);
		if (!current || (current.authority !== null && tax.authority === null)) {
			byCountry.set(tax.countryCode, tax);
		}
	}
	return [...byCountry.values()];
};

export const fromPersonTaxInformationList = (
	saved: GetPersonTaxInformationRes[]
): TaxResidenceEntry[] =>
	dedupeSavedTaxResidencesByCountry(saved).map((tax) => ({
		taxId: tax.id,
		taxResidency: tax.countryCode,
		tin: tax.number ?? '',
	}));

const toTaxInformationBody = (
	entry: TaxResidenceEntry,
	includeTin: boolean
): CreatePersonTaxInformationDto => ({
	countryCode: entry.taxResidency,
	number: includeTin ? entry.tin || null : null,
	authority: null,
});

/**
 * Diffs the saved tax residences against the submitted entries: entries
 * without a matching saved record are created, and saved records no longer
 * present are deleted. `includeTin` mirrors whether the TIN field is
 * collected:
 * - with TIN (edit mode): entries whose country/TIN changed are updated;
 * - country-only (add/remove mode): matched saved records are left untouched —
 *   the UI collects no edits for them, and a record can carry data the form
 *   doesn't show (a TIN entered elsewhere) that a write would wipe.
 *
 * Entries are matched to saved records by taxId, and id-less entries by
 * country as a fallback (an entry can lack the taxId of an existing record,
 * e.g. when the form was restored from the in-flight backup). The unique is
 * (personId, countryCode, authority), so a country can hold an
 * authority-stamped record next to the authority-less one the form shows:
 * matching and updates run against one representative per country, while
 * removal applies to every record of a dropped country — a hidden pair member
 * must neither resurface after a remove nor be deleted while its country is
 * still submitted.
 */
export const diffTaxResidences = (
	saved: GetPersonTaxInformationRes[],
	entries: TaxResidenceEntry[],
	includeTin: boolean
): TaxResidencesDiff => {
	const filled = entries.filter((entry) => entry.taxResidency);
	const visible = dedupeSavedTaxResidencesByCountry(saved);
	const savedById = new Map(visible.map((tax) => [tax.id, tax]));
	const claimedIds = new Set(
		filled
			.map((entry) => entry.taxId)
			.filter((taxId): taxId is string => !!taxId && savedById.has(taxId))
	);

	const toCreate: TaxResidencesDiff['toCreate'] = [];
	const toUpdate: TaxResidencesDiff['toUpdate'] = [];

	for (const entry of filled) {
		let savedTax = entry.taxId ? savedById.get(entry.taxId) : undefined;
		if (!savedTax) {
			savedTax = visible.find(
				(tax) => !claimedIds.has(tax.id) && tax.countryCode === entry.taxResidency
			);
			if (savedTax) {
				claimedIds.add(savedTax.id);
			}
		}
		if (!savedTax) {
			toCreate.push(toTaxInformationBody(entry, includeTin));
			continue;
		}
		if (!includeTin) {
			continue;
		}
		const body = toTaxInformationBody(entry, includeTin);
		const isUnchanged =
			savedTax.countryCode === body.countryCode && (savedTax.number ?? null) === body.number;
		if (!isUnchanged) {
			toUpdate.push({ taxId: savedTax.id, body });
		}
	}

	const keptCountries = new Set(filled.map((entry) => entry.taxResidency));
	const toDeleteIds = saved
		.filter((tax) => !claimedIds.has(tax.id) && !keptCountries.has(tax.countryCode))
		.map((tax) => tax.id);

	return { toCreate, toUpdate, toDeleteIds };
};
